import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LoadingService } from '@app/shared/services/loading.service';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { IScreeningDivergenceReportResponse } from '../../models';
import Utils from '@app/shared/utils/utils';
import { take } from 'rxjs';
import { FileUploadService } from '@app/shared/services/file-upload/file-upload.service';

@Component({
  selector: 'app-modal-divergence-report-preview',
  templateUrl: './modal-divergence-report-preview.component.html',
  styleUrls: ['./modal-divergence-report-preview.component.sass'],
})
export class ModalDivergenceReportPreviewComponent {
  printDisabled = false;
  divergenceReportBlob: Blob;
  reportFilename: string;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IScreeningDivergenceReportResponse,
    private dialogRef: MatDialogRef<ModalDivergenceReportPreviewComponent>,
    private _loadingService: LoadingService,
    private _uploadService: FileUploadService,
    private loading: LoadingService
  ) {
    this.loading.forceLoading = false;
  }

  async doCloseScreening() {
    if (!this.divergenceReportBlob) {
      await this.generateDivergenceReport();
    }
    await this.uploadEvidencesAndCloseDialog();
  }

  private async generateDivergenceReport() {
    this.printDisabled = true;
    this._loadingService.show();
    const divergenceReportFile = await this.generatePdf();
    this.divergenceReportBlob = divergenceReportFile.output('blob');
    this.reportFilename = Utils.randomFilenameGenerator(
      `laudo-${this.data.separateReport ? `complementar_${this.data.token}` : 'divergencia'}_${
        this.data.trade.tradeId
      }`,
      'pdf'
    );
    this._loadingService.hide();
    this.printDisabled = false;
  }

  private async uploadEvidencesAndCloseDialog() {
    this.loading.forceLoading = true;
    this.loading.show();
    try {
      const uploadRequests = this.data.evidences.map(async (evidence) => {
        if (evidence.src.startsWith('http') || evidence.src.startsWith('https')) {
          return;
        }

        const fileName = Utils.randomFilenameGenerator(evidence.description, 'png');
        const fileNamePath = `${this.data.trade.tradeId}/${fileName}`;
        const payload: { idTrade: string; filename: string; contentType: string } = {
          idTrade: this.data.trade.tradeId,
          filename: fileNamePath,
          contentType: this.getContentTypeFrom(evidence.src),
        };

        const response = await this._uploadService
          .postGeneratePresignedUrlUpload(payload, 'COLLECT.postGeneratePresignedUrl')
          .pipe(take(1))
          .toPromise();

        const imageFile: File | any = await this.dataURItoFile(evidence.src, fileName);

        const res = await this._uploadService
          .uploadFile(imageFile, response.url)
          .pipe(take(1))
          .toPromise();

        await this._uploadService
          .updateTradeGallery(
            { idTrade: this.data.trade.tradeId, filename: fileNamePath },
            'COLLECT.postRegisterUploadedMedia'
          )
          .pipe(take(1))
          .toPromise();
      });

      await Promise.all(uploadRequests);

      this.dialogRef.close({
        idTrade: this.data.trade.tradeId,
        action: 'closeScreeing',
        divergenceReportFile: this.divergenceReportBlob,
        filename: this.reportFilename,
      });

      this.loading.hide();
    } catch (error) {
      console.error('Erro ao fazer upload das imagens:', error);
    }
  }

  async addContentToPdf(
    pdf: jsPDF,
    elementId: string,
    startY: number,
    elementSrc?: HTMLElement,
    extraHeader?: string
  ): Promise<number> {
    let element: HTMLElement;
    if (elementSrc) {
      element = elementSrc;
    } else {
      element = document.getElementById(elementId);
    }

    const images: any = element.getElementsByTagName('img');
    for (const img of images) {
      if (
        !img.classList.contains('img-logo') &&
        (img.src.startsWith('http') || img.src.startsWith('https'))
      ) {
        const base64Image = await this.convertImageUrlToBase64(img.src);
        img.src = base64Image;
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(element, { scale: 3 });
    const contentDataURL = canvas.toDataURL('image/png', 1.0);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const availableWidth = pageWidth - margin * 2;
    const availableHeight = pageHeight - margin * 2;

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
    const finalWidth = imgWidth * ratio;
    const finalHeight = imgHeight * ratio;

    const marginX = margin + (availableWidth - finalWidth) / 2;
    const marginY = startY;

    if (marginY + finalHeight > pageHeight - margin) {
      pdf.addPage();
      startY = margin;
    }

    pdf.addImage(
      contentDataURL,
      'PNG',
      marginX,
      startY,
      finalWidth,
      finalHeight,
      undefined,
      'SLOW'
    );

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');

    return startY + finalHeight + 3;
  }

  async convertImageUrlToBase64(url: string): Promise<string> {
    const image = await fetch(url, { cache: 'no-store' });
    const blob = await image.blob();
    const reader = new FileReader();

    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(blob);
    });
  }

  async printToPdf() {
    this.printDisabled = true;
    this._loadingService.show();
    const pdf = await this.generatePdf();
    this._loadingService.hide();
    this.printDisabled = false;
    this.divergenceReportBlob = pdf.output('blob');
    this.reportFilename = Utils.randomFilenameGenerator(
      `laudo-${this.data.separateReport ? `complementar_${this.data.token}` : 'divergencia'}_${
        this.data.trade.tradeId
      }`,
      'pdf'
    );
    pdf.save(this.reportFilename);
  }

  async generatePdf() {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let currentY = 10;
    const parts = [
      'header',
      'orderdescription',
      'customerinfo',
      'productinfo',
      'questions',
      'divergencereasons',
      'gallerytitle',
    ];

    for (const elementName of parts) {
      if (elementName !== 'header' && currentY === 10) {
        currentY = await this.addContentToPdf(pdf, 'header', currentY);
      }
      currentY = await this.addContentToPdf(pdf, elementName, currentY);
    }

    const pdfRows = document.getElementsByClassName('pdf-row');
    const images = Array.from(pdfRows) as HTMLElement[];

    for (const element of images) {
      currentY = await this.addContentToPdf(pdf, null, currentY, element, 'gallerytitle');
    }

    return pdf;
  }

  async dataURItoFile(dataURI: string, fileName: string): Promise<File> {
    const mimeType = this.getContentTypeFrom(dataURI);
    const res: Response = await fetch(dataURI);
    const blob: Blob = await res.blob();
    return new File([blob], fileName, { type: mimeType });
  }

  private getContentTypeFrom(dataURI: string): string {
    const mimeTypeMatch = dataURI.match(/data:(image\/[\w+]+);base64,/);
    return mimeTypeMatch ? mimeTypeMatch[1] : 'image/png';
  }
}
